/**
 * AdsCampaignsComparePage — Tela de comparacao de campanhas (/ads/campaigns/compare).
 *
 * Fonte da verdade do fluxo/estados:
 *   docs/execucao-projetos/_local/design/wireframes/2026-06-09-analise-campanha-isolada.md §2.3
 *
 * Contrato de API consumido:
 *   GET /ads/campaigns/compare?ids=id1,id2&period=30d  → CampaignCompareResponse
 *
 * Estados honestos implementados:
 *   loading          → SkeletonTabela + skeleton graficos
 *   ready            → tabela lado a lado + graficos + bloco CROSS
 *   ready-mixed      → celulas sem dado exibem "—" (nunca "0")
 *   single-campaign  → redireciona para detalhe (invalido para compare)
 *   error            → mensagem + retry
 *
 * Invariantes:
 *   - Maximo 3 campanhas (D-CAMP-04: botao "Adicionar" oculto apos 3)
 *   - ROAS sempre null (R-063) → "—"
 *   - comparison_insight null → bloco CROSS nao renderiza
 *   - Cores: campanha 1 = roxo #3F2570, 2 = azul, 3 = rosa
 *   - A11y AA minimo
 *
 * F2 — 2026-06-09
 */

import '../styles/screen-ads.css';
import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { t } from '../lib/i18n';
import { apiClient, ApiError } from '../lib/api-client';
import { Skeleton } from '../components/Skeleton';
import { Sparkline } from '../components/Sparkline';
import {
  fmtCurrency,
  fmtNumber,
  fmtPercent,
  statusLabel,
  statusCssClass,
  AdsRefPanel,
} from './AdsCampaignsListPage';
import type { CampaignKpis, CampaignItem, RefPanelData } from './AdsCampaignsListPage';
import type { CampaignSeries } from './AdsCampaignDetailPage';

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

type CampaignCompareItem = CampaignItem & { readonly series: CampaignSeries };

interface CampaignCompareResponse {
  readonly period_from: string;
  readonly period_to: string;
  readonly campaigns: readonly CampaignCompareItem[];
  readonly comparison_insight: string | null;
}

// ---------------------------------------------------------------------------
// Load state
// ---------------------------------------------------------------------------

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; data: CampaignCompareResponse }
  | { kind: 'error'; failedIds?: readonly string[] }
  | { kind: 'invalid' };

// ---------------------------------------------------------------------------
// Cores das campanhas (D-CAMP-04: 3 campanhas maximo no lancamento)
// ---------------------------------------------------------------------------

const CAMPAIGN_COLORS = ['#3F2570', '#0079DC', '#F9005C'] as const;

// ---------------------------------------------------------------------------
// Metricas da tabela de comparacao
// ---------------------------------------------------------------------------

interface CompareMetric {
  readonly key: keyof CampaignKpis;
  readonly label: string;
  readonly fmt: (v: number | null) => string;
  // Se menor = melhor (ex: CPC) ou maior = melhor (ex: cliques)
  readonly lowerIsBetter?: boolean;
}

const COMPARE_METRICS: readonly CompareMetric[] = [
  { key: 'spend', label: 'ads.kpiSpend', fmt: fmtCurrency },
  { key: 'clicks', label: 'ads.kpiClicks', fmt: fmtNumber },
  { key: 'cpc', label: 'ads.kpiCpc', fmt: fmtCurrency, lowerIsBetter: true },
  { key: 'ctr', label: 'ads.kpiCtr', fmt: fmtPercent },
  { key: 'impressions', label: 'ads.kpiImpressions', fmt: fmtNumber },
  { key: 'roas', label: 'ads.kpiRoas', fmt: () => '—' },
  { key: 'cpm', label: 'ads.kpiCpm', fmt: fmtCurrency, lowerIsBetter: true },
] as const;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function buildCompareUrl(ids: readonly string[], period: string): string {
  return `/ads/campaigns/compare?ids=${ids.join(',')}&period=${period}`;
}

function filterPoints(arr: ReadonlyArray<number | null>): readonly number[] {
  return arr.filter((v): v is number => v !== null);
}

// Determina qual campanha tem o melhor valor numa metrica
// Retorna o indice (0-based) ou -1 se nao ha dado comparavel
function bestValueIndex(
  campaigns: readonly CampaignCompareItem[],
  key: keyof CampaignKpis,
  lowerIsBetter = false,
): number {
  const values = campaigns.map((c) => c.kpis[key]);
  const nonNull = values.filter((v): v is number => v !== null);
  if (nonNull.length < 2) return -1;

  const best = lowerIsBetter
    ? Math.min(...(nonNull as number[]))
    : Math.max(...(nonNull as number[]));
  const idx = values.findIndex((v) => v === best);
  return idx;
}

// ---------------------------------------------------------------------------
// Skeleton da comparacao
// ---------------------------------------------------------------------------

function AdsCompareSkeleton(): JSX.Element {
  return (
    <div role="status" aria-label="Carregando comparacao" aria-busy="true">
      {/* Headers skeleton */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 'var(--mkv-space-4)',
          marginBottom: 'var(--mkv-space-5)',
        }}
        aria-hidden="true"
      >
        {[0, 1].map((i) => (
          <div
            key={i}
            style={{
              background: 'var(--md-surface-container-low)',
              borderRadius: 'var(--md-shape-md)',
              padding: 'var(--mkv-space-4)',
              height: 80,
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--mkv-space-2)',
            }}
          >
            <Skeleton variant="heading" />
            <Skeleton variant="text" width="50%" />
          </div>
        ))}
      </div>
      {/* Tabela skeleton */}
      <div
        style={{
          border: '1px solid var(--md-outline-variant)',
          borderRadius: 'var(--md-shape-md)',
          overflow: 'hidden',
        }}
        aria-hidden="true"
      >
        {[0, 1, 2, 3, 4, 5, 6].map((i) => (
          <div
            key={i}
            style={{
              display: 'grid',
              gridTemplateColumns: '160px 1fr 1fr',
              gap: 'var(--mkv-space-4)',
              padding: 'var(--mkv-space-3) var(--mkv-space-4)',
              borderBottom: '1px solid var(--md-outline-variant)',
            }}
          >
            <Skeleton variant="text" width="80%" />
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="text" width="60%" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

const MAX_CAMPAIGNS = 3;

export function AdsCampaignsComparePage(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const idsParam = searchParams.get('ids') ?? '';
  const ids = idsParam
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  const period = searchParams.get('period') ?? '30d';

  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [retryKey, setRetryKey] = useState<number>(0);
  const [refPanelData, setRefPanelData] = useState<RefPanelData | null>(null);

  // Redireciona se apenas 1 campanha (invalido para compare)
  useEffect(() => {
    if (ids.length === 1) {
      void navigate(`/digital/campanhas/${ids[0]}?period=${period}`, { replace: true });
    }
    if (ids.length === 0) {
      setState({ kind: 'invalid' });
    }
  }, [ids.length, ids[0], navigate, period]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (ids.length < 2) return;

    let cancelled = false;
    setState({ kind: 'loading' });

    const load = async (): Promise<void> => {
      try {
        const data = await apiClient.get<CampaignCompareResponse>(buildCompareUrl(ids, period));
        if (cancelled) return;

        if (data.campaigns.length === 0) {
          setState({ kind: 'error', failedIds: ids });
          return;
        }

        setState({ kind: 'ready', data });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 400) {
          setState({ kind: 'invalid' });
          return;
        }
        setState({ kind: 'error' });
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [idsParam, period, retryKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRemoveCampaign = useCallback(
    (removeId: string) => {
      const newIds = ids.filter((x) => x !== removeId);
      if (newIds.length === 1) {
        // Redireciona para detalhe quando so resta uma campanha
        void navigate(`/digital/campanhas/${newIds[0]}?period=${period}`, { replace: true });
      } else if (newIds.length === 0) {
        void navigate('/digital/campanhas', { replace: true });
      } else {
        setSearchParams({ ids: newIds.join(','), period });
      }
    },
    [ids, navigate, period, setSearchParams],
  );

  const handleRefClick = useCallback(
    (metric: string, campaign: CampaignCompareItem, periodFrom: string, periodTo: string) => {
      setRefPanelData({
        metric,
        campaignName: campaign.name,
        refId: campaign.ref_id,
        periodFrom,
        periodTo,
      });
    },
    [],
  );

  const handleAskCross = useCallback(() => {
    const contextIds = ids.join(',');
    void navigate(`/chat?context=compare:${contextIds}`);
  }, [navigate, ids]);

  // ---------------------------------------------------------------------------
  // Renders por estado
  // ---------------------------------------------------------------------------

  if (state.kind === 'invalid') {
    return (
      <div>
        <nav className="ads-breadcrumb" aria-label="Navegacao">
          <Link to="/digital/campanhas">
            <span className="material-symbols-rounded" style={{ fontSize: 14 }} aria-hidden="true">
              arrow_back
            </span>
            {t('ads.backToCampaigns')}
          </Link>
        </nav>
        <div className="ads-empty-state">
          <span className="ads-empty-state-icon material-symbols-rounded" aria-hidden="true">
            compare_arrows
          </span>
          <p className="ads-empty-state-desc">{t('ads.compareTitleCount', { count: '0' })}</p>
          <div className="ads-empty-state-actions">
            <Link to="/digital/campanhas" className="ads-btn-primary">
              {t('ads.backToCampaigns')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (state.kind === 'loading') {
    return (
      <div>
        <nav className="ads-breadcrumb" aria-label="Navegacao">
          <Link to="/digital/campanhas">
            <span className="material-symbols-rounded" style={{ fontSize: 14 }} aria-hidden="true">
              arrow_back
            </span>
            {t('ads.backToCampaigns')}
          </Link>
        </nav>
        <div className="ads-page-header">
          <div className="ads-page-eyebrow">Meta Ads</div>
          <h1 className="ads-page-title">{t('ads.compareTitle')}</h1>
        </div>
        <AdsCompareSkeleton />
      </div>
    );
  }

  if (state.kind === 'error') {
    return (
      <div>
        <nav className="ads-breadcrumb" aria-label="Navegacao">
          <Link to="/digital/campanhas">
            <span className="material-symbols-rounded" style={{ fontSize: 14 }} aria-hidden="true">
              arrow_back
            </span>
            {t('ads.backToCampaigns')}
          </Link>
        </nav>
        <div className="ads-empty-state" role="alert">
          <span className="ads-empty-state-icon material-symbols-rounded" aria-hidden="true">
            wifi_off
          </span>
          <h1 className="ads-empty-state-title">{t('ads.errorTitle')}</h1>
          <p className="ads-empty-state-desc">{t('ads.errorDesc')}</p>
          <div className="ads-empty-state-actions">
            <button
              type="button"
              className="ads-btn-primary"
              onClick={() => setRetryKey((k) => k + 1)}
            >
              {t('ads.errorRetry')}
            </button>
            <Link to="/digital/campanhas" className="ads-btn-secondary">
              {t('ads.backToCampaigns')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // state.kind === 'ready'
  const { campaigns, period_from, period_to, comparison_insight } = state.data;
  const count = campaigns.length;

  return (
    <div>
      {/* Breadcrumb */}
      <nav className="ads-breadcrumb" aria-label="Navegacao">
        <Link to="/digital/campanhas">
          <span className="material-symbols-rounded" style={{ fontSize: 14 }} aria-hidden="true">
            arrow_back
          </span>
          {t('ads.backToCampaigns')}
        </Link>
      </nav>

      {/* Page header */}
      <div className="ads-page-header">
        <div className="ads-page-eyebrow">Meta Ads</div>
        <h1 className="ads-page-title">
          {t('ads.compareTitleCount', { count: String(count) })} · {period_from} — {period_to}
        </h1>
      </div>

      {/* Filtro de periodo */}
      <div className="ads-filters" role="group" aria-label={t('ads.filterPeriod')}>
        <div className="ads-filter-group">
          <label htmlFor="ads-compare-period-select" className="ads-filter-label">
            {t('ads.filterPeriod')}:
          </label>
          <select
            id="ads-compare-period-select"
            className="ads-filter-select"
            value={period}
            onChange={(e) => {
              setSearchParams({ ids: ids.join(','), period: e.target.value });
            }}
            aria-label={t('ads.filterPeriod')}
          >
            <option value="7d">{t('ads.period7d')}</option>
            <option value="30d">{t('ads.period30d')}</option>
            <option value="90d">{t('ads.period90d')}</option>
            <option value="365d">{t('ads.period365d')}</option>
          </select>
        </div>

        {/* D-CAMP-04: "Adicionar campanha" oculto apos 3 campanhas */}
        {count < MAX_CAMPAIGNS ? (
          <Link
            to={`/digital/campanhas?period=${period}`}
            className="ads-btn-secondary"
            aria-label={t('ads.compareAddCampaign')}
          >
            <span className="material-symbols-rounded" style={{ fontSize: 14 }} aria-hidden="true">
              add
            </span>
            {t('ads.compareAddCampaign')}
          </Link>
        ) : null}
      </div>

      {/* Cabecalhos das campanhas */}
      <div
        className={`ads-compare-headers count-${count}`}
        role="list"
        aria-label="Campanhas sendo comparadas"
      >
        {campaigns.map((campaign, idx) => (
          <div
            key={campaign.id}
            className="ads-compare-campaign-card"
            role="listitem"
            style={{ borderTop: `4px solid ${CAMPAIGN_COLORS[idx] ?? CAMPAIGN_COLORS[0]}` }}
          >
            <div className="ads-compare-campaign-card-name">{campaign.name}</div>
            <div className="ads-compare-campaign-card-meta">
              <span className={`ads-status-badge ${statusCssClass(campaign.status)}`}>
                {statusLabel(campaign.status)}
              </span>
            </div>
            <button
              type="button"
              className="ads-compare-remove"
              onClick={() => handleRemoveCampaign(campaign.id)}
              aria-label={`${t('ads.compareRemove')}: ${campaign.name}`}
            >
              ×
            </button>
          </div>
        ))}
      </div>

      {/* Tabela de comparacao */}
      <div className="ads-compare-table-wrapper">
        <table className="ads-compare-table" aria-label={t('ads.compareTitle')}>
          <thead>
            <tr>
              <th scope="col">{t('ads.compareMetric')}</th>
              {campaigns.map((c, idx) => (
                <th
                  key={c.id}
                  scope="col"
                  style={{ color: CAMPAIGN_COLORS[idx] ?? CAMPAIGN_COLORS[0] }}
                >
                  {c.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {COMPARE_METRICS.map((metric) => {
              const bestIdx = bestValueIndex(campaigns, metric.key, metric.lowerIsBetter);
              return (
                <tr key={metric.key}>
                  <td className="col-metric">{t(metric.label as 'ads.kpiSpend')}</td>
                  {campaigns.map((c, idx) => {
                    const v = c.kpis[metric.key];
                    const isBest = idx === bestIdx;
                    const displayValue =
                      metric.key === 'roas' ? '—' : metric.fmt(v as number | null);
                    return (
                      <td key={c.id} className={isBest ? 'best-value' : ''}>
                        {v !== null && metric.key !== 'roas' ? (
                          <span>
                            {displayValue}{' '}
                            <button
                              type="button"
                              className="ads-ref-chip"
                              onClick={() =>
                                handleRefClick(
                                  t(metric.label as 'ads.kpiSpend'),
                                  c,
                                  period_from,
                                  period_to,
                                )
                              }
                              aria-label={`${t('ads.refChipLabel')} — ${t(metric.label as 'ads.kpiSpend')} — ${c.name}`}
                            >
                              [ref:]
                            </button>
                          </span>
                        ) : (
                          <span
                            title={metric.key === 'roas' ? t('ads.roasNullExplain') : undefined}
                          >
                            {displayValue}
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Graficos comparativos */}
      <section className="ads-compare-charts" aria-label="Graficos comparativos">
        {/* Legenda */}
        <div className="ads-compare-chart-legend">
          {campaigns.map((c, idx) => (
            <div key={c.id} className="ads-compare-chart-legend-item">
              <div
                className="ads-compare-chart-legend-dot"
                style={{ background: CAMPAIGN_COLORS[idx] ?? CAMPAIGN_COLORS[0] }}
                aria-hidden="true"
              />
              {c.name}
            </div>
          ))}
        </div>

        {/* Cliques ao longo do periodo */}
        <div className="ads-compare-chart-title">{t('ads.compareChartClicks')}</div>
        <div className="ads-multi-sparkline-wrapper">
          {campaigns.map((c) => {
            const pts = filterPoints(c.series.clicks);
            if (pts.length < 2) return null;
            return (
              <Sparkline
                key={c.id}
                points={pts as number[]}
                width={320}
                height={60}
                ariaLabel={`${t('ads.kpiClicks')} — ${c.name}`}
              />
            );
          })}
        </div>

        {/* CPC ao longo do periodo */}
        <div className="ads-compare-chart-title">{t('ads.compareChartCpc')}</div>
        <div className="ads-multi-sparkline-wrapper">
          {campaigns.map((c) => {
            const pts = filterPoints(c.series.cpc);
            if (pts.length < 2) return null;
            return (
              <Sparkline
                key={c.id}
                points={pts as number[]}
                width={320}
                height={60}
                ariaLabel={`${t('ads.kpiCpc')} — ${c.name}`}
              />
            );
          })}
        </div>
      </section>

      {/* Bloco CROSS insight da comparacao — so renderiza quando nao for null */}
      {comparison_insight !== null && comparison_insight.length > 0 ? (
        <section className="ads-cross-insight" aria-labelledby="ads-compare-insight-title">
          <div className="ads-cross-insight-title" id="ads-compare-insight-title">
            {t('ads.compareInsightTitle')}
          </div>
          <p className="ads-cross-insight-text">{comparison_insight}</p>
          <div className="ads-cross-insight-actions">
            <button
              type="button"
              className="ads-ref-chip"
              style={{ display: 'inline-flex' }}
              aria-label={t('ads.refChipLabel')}
            >
              [ref: Meta Ads · {period_from}–{period_to}]
            </button>
            <button type="button" className="ads-cross-insight-ask" onClick={handleAskCross}>
              <span
                className="material-symbols-rounded"
                style={{ fontSize: 14 }}
                aria-hidden="true"
              >
                forum
              </span>
              {t('ads.compareAskMore')}
            </button>
          </div>
        </section>
      ) : null}

      {/* Painel de rastreabilidade */}
      {refPanelData !== null ? (
        <AdsRefPanel data={refPanelData} onClose={() => setRefPanelData(null)} />
      ) : null}
    </div>
  );
}
